import { evaluateCondition } from 'src/utils/conditionEvaluator'

/** Minimal question shape for visibility; aligned with `Question` in interfaces/question.d.ts */
export type QuestionLike = {
  id?: number | string | null
  temp_id?: string | number | null
  type?: string
  parent_question_id?: number | string | null
  parent_temp_id?: string | number | null
  depth?: number
  display_conditions?: unknown
}

export type QuestionListInput = QuestionLike[] | null | undefined

export type AnswerPayloadLike = {
  question_id?: number | string | null
  temp_id?: string | number | null
  answer: unknown
}

/** Builds the answers map used by visibility checks (form field id → answer value). */
export const buildAnswersMapFromFormValues = (
  formValues: Record<string, unknown>,
): Record<string, unknown> => formValues

export const buildAnswersMapFromAnswerPayload = (
  answers: AnswerPayloadLike[],
): Record<string, unknown> =>
  Object.fromEntries(
    answers.map((a) => [String(a.question_id || a.temp_id || ''), a.answer]),
  )

/**
 * Factory for recursive sub-question visibility (same rules as Automatic Plan UI).
 */
export const createCheckQuestionVisibility = (
  questionList: QuestionListInput,
) => {
  const checkVisibility = (
    q: QuestionLike,
    answers: Record<string, unknown>,
  ): boolean => {
    const parentId = q.parent_question_id || q.parent_temp_id
    if (!parentId) {
      return (q.depth || 0) === 0
    }

    const parent = questionList?.find(
      (pq) => String(pq.id || pq.temp_id) == String(parentId),
    )
    if (!parent) {
      return false
    }

    return (
      checkVisibility(parent, answers) &&
      evaluateCondition(
        q.display_conditions as Parameters<typeof evaluateCondition>[0],
        answers,
        parentId,
      )
    )
  }

  return checkVisibility
}

export const isQuestionVisible = (
  question: QuestionLike,
  questionList: QuestionListInput,
  answersMap: Record<string, unknown>,
): boolean => createCheckQuestionVisibility(questionList)(question, answersMap)

const isFormAnswerEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const emptyValueForQuestionType = (questionType?: string): unknown =>
  questionType === 'checkbox' ? [] : null

/**
 * Clears form values for hidden sub-questions (one pass). Returns true if any field changed.
 */
export const clearHiddenSubQuestionFormValues = (
  questions: QuestionListInput,
  formValues: Record<string, unknown>,
  setFieldValue?: (fieldName: string, value: unknown) => void,
): boolean => {
  if (!questions?.length) {
    return false
  }

  const checkVisibility = createCheckQuestionVisibility(questions)
  let changed = false

  questions.forEach((q) => {
    const fieldName = String(q.id || q.temp_id)
    if (checkVisibility(q, formValues)) {
      return
    }

    const currentValue = formValues[fieldName]
    if (!isFormAnswerEmpty(currentValue)) {
      const emptyValue = emptyValueForQuestionType(q.type)
      formValues[fieldName] = emptyValue
      setFieldValue?.(fieldName, emptyValue)
      changed = true
    }
  })

  return changed
}

/** Re-runs hidden-field cleanup until stable (parent answer changes can cascade). */
export const clearHiddenSubQuestionsRecursively = (
  questions: QuestionListInput,
  formValues: Record<string, unknown>,
  setFieldValue?: (fieldName: string, value: unknown) => void,
): void => {
  while (
    clearHiddenSubQuestionFormValues(questions, formValues, setFieldValue)
  ) {
    // cascade until no more hidden fields hold values
  }
}

/** Drops answers for questions that fail the visibility chain (matches API filter intent). */
export const filterVisibleAnswerPayload = <T extends AnswerPayloadLike>(
  questions: QuestionListInput,
  answers: T[],
): T[] => {
  if (!questions?.length) {
    return answers
  }

  const checkVisibility = createCheckQuestionVisibility(questions)
  const answersMap = buildAnswersMapFromAnswerPayload(answers)

  return answers.filter((entry) => {
    const q = questions.find(
      (item) =>
        (entry.question_id != null &&
          String(item.id) === String(entry.question_id)) ||
        (entry.temp_id != null &&
          String(item.temp_id) === String(entry.temp_id)),
    )
    if (!q) {
      return true
    }
    return checkVisibility(q, answersMap)
  })
}
